import type { PushSdkInternals } from '../push-sdk-internal-impl'
import { type Logger, TaggedLogger } from '@core/logging/logger'
import { ShowPromptOptions } from '../prompts/prompt-controller.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { SubscriberStatus } from '../enums/subscriber-status.ts'

/** @public */
export type PushNotifications = {
    /**
     * Triggers the PushSDK to display the next eligible prompt. By default,
     * all standard eligibility checks will be performed. To skip those checks
     * and force display of the prompt you can specify `skipEligibilityCheck: true`
     * in the {@link ShowPromptOptions}. eg:
     * ```ts
     * PushlySDK.PushNotifications.showPermissionPrompt({ skipEligibilityCheck: true })
     * ```
     *
     * <b>Note:</b> Setting `skipEligibilityCheck` to `true` still adheres to the single
     * prompt rule and does not allow for showing more than one prompt at a time.
     *
     * @param {ShowPromptOptions} [options]
     */
    showPermissionPrompt(options?: ShowPromptOptions): void

    /**
     * Triggers the PushSDK to display the specified prompt. By default,
     * all standard eligibility checks will be performed. To skip those checks
     * and force display of the prompt you can specify `skipEligibilityCheck: true`
     * in the {@link ShowPromptOptions}. eg:
     * ```ts
     * PushlySDK.PushNotifications.showPermissionPrompt(id, { skipEligibilityCheck: true })
     * ```
     *
     * <b>Note:</b> Setting `skipEligibilityCheck` to `true` still adheres to the single
     * prompt rule and does not allow for showing more than one prompt at a time.
     *
     * @param {number} id
     * @param {ShowPromptOptions} [options]
     */
    showPermissionPrompt(id: number, options?: ShowPromptOptions): void

    /**
     * Pauses delivery of push notifications for the current subscriber.
     */
    pause(): Promise<void>

    /**
     * Resumes delivery of push notifications for the current subscriber.
     */
    resume(): Promise<void>

    /**
     * Returns if the current subscriber has push notifications paused.
     */
    getIsPaused(): Promise<boolean>

    /**
     * Returns if the current user is eligible to be prompted for push notification permissions.
     */
    getIsEligibleToPrompt(): Promise<boolean>

    /**
     * Returns if the current user is subscribed.
     */
    getIsSubscribed(): Promise<boolean>

    /**
     * Suppresses all Pushly prompting (automatic and manual) until
     * {@link allowPrompts} is called. Use this to block prompts while a
     * checkout or other on-page flow is active. Resets on full page reload.
     */
    suppressPrompts(): void

    /**
     * Re-allows Pushly prompting after a call to {@link suppressPrompts}.
     */
    allowPrompts(): void

    /**
     * Returns whether prompting is currently suppressed via {@link suppressPrompts}, or because the
     * SDK has exited, for example on a browser that opted out.
     */
    getIsPromptingSuppressed(): boolean
}

/** @internal */
export class PushNotificationsImpl implements PushNotifications {
    readonly #sdk: PushSdkInternals
    readonly #logger: Logger

    public constructor(sdk: PushSdkInternals, logger?: Logger) {
        this.#sdk = sdk
        this.#logger = logger ?? TaggedLogger._getInstance('Notifications')
    }

    public showPermissionPrompt(options?: ShowPromptOptions): void
    public showPermissionPrompt(id: number, options?: ShowPromptOptions): void
    public showPermissionPrompt(idOrOptions?: number | ShowPromptOptions, options?: ShowPromptOptions): void {
        let id: number | undefined
        let opts: ShowPromptOptions = {}

        if (typeof idOrOptions === 'number') {
            id = idOrOptions
            opts = options ?? {}
        } else {
            opts = idOrOptions ?? {}
        }

        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this.#sdk.promptsController?._showPrompt(id, opts)
        })
    }

    public async getIsEligibleToPrompt(): Promise<boolean> {
        const isTrackingHalted = await this.#sdk.userStore._getIsTrackingHalted()
        const isSubscribed = await this.#sdk.userStore._getIsSubscribed()
        const isUserInSoftUnsubscribedState = await this.#sdk.userStore._getIsUserInSoftUnsubscribedState()
        const subscriberStatus = await this.#sdk.userStore._getSubscriberStatus()

        return (
            this.#sdk.isLoaded &&
            this.#sdk.isEnabled &&
            !isTrackingHalted &&
            !isSubscribed &&
            subscriberStatus != SubscriberStatus.DENIED &&
            subscriberStatus != SubscriberStatus.DISMISSED &&
            !isUserInSoftUnsubscribedState
        )
    }

    public async pause(): Promise<void> {
        this._executeIfSubscribed(async () => {
            const isPaused = await this.#sdk.userStore._getIsUserInSoftUnsubscribedState()
            if (isPaused) {
                this.#logger.info('The user is already in soft unsubscribe status.')
                return
            }

            this.#sdk.userStore._setIsUserInSoftUnsubscribedState()
            this.#sdk.eventManager._track(TrackedEventAction.SOFT_UNSUBSCRIBE)

            this.#logger.info("The user's notifications have been paused.")
        })
    }

    public async resume(): Promise<void> {
        this._executeIfSubscribed(async () => {
            const isPaused = await this.#sdk.userStore._getIsUserInSoftUnsubscribedState()
            if (!isPaused) {
                this.#logger.info('The user is not in a soft unsubscribe status.')
                return
            }

            this.#sdk.userStore._unsetIsUserInSoftUnsubscribedState()
            this.#sdk.eventManager._track(TrackedEventAction.SOFT_RESUBSCRIBE)

            this.#logger.info("The user's notifications have been resumed.")
        })
    }

    public async getIsPaused(): Promise<boolean> {
        return this.#sdk.userStore._getIsUserInSoftUnsubscribedState()
    }

    public async getIsSubscribed(): Promise<boolean> {
        // The stored status is not a reliable answer once tracking halts: the halt clears it to
        // not-determined, and a sibling host's stored `subscribed` wins arbitration over that.
        if (await this.#sdk.userStore._getIsTrackingHalted()) {
            return false
        }

        return this.#sdk.userStore._getIsSubscribed()
    }

    public suppressPrompts(): void {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this.#sdk.promptsController?._suppressPrompts()
        })
    }

    public allowPrompts(): void {
        this.#sdk._executeWhenConfiguredIfEnabled(() => {
            this.#sdk.promptsController?._allowPrompts()
        })
    }

    public getIsPromptingSuppressed(): boolean {
        // An exited SDK never prompts, including one that exited before its prompts controller
        // existed, such as the halted load of an opted-out browser.
        if (this.#sdk.isExited) {
            return true
        }

        return this.#sdk.promptsController?._getIsPromptingSuppressed() ?? false
    }

    protected async _executeIfSubscribed(action: () => Promise<void> | void): Promise<void> {
        await this.#sdk._executeWhenConfigured(async () => {
            const isSubscribed = await this.getIsSubscribed()
            if (!isSubscribed) {
                this.#logger.warn('User is not currently subscribed and can not use this method')
                return
            }

            try {
                await action()
            } catch (err) {
                const error = ensureError(err)
                this.#logger.error(error)
                this.#sdk.eventManager._track(error)
            }
        })
    }
}
